import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { isEmpty, get, map, merge, groupBy, reduce, forEach } from 'lodash';
import { formatDataValue } from '@/helpers/format';
import { GroupedPortfolio, Portfolio } from '@/types/Portfolio';
import { config } from '@/services/config';
import {
  ApprovalRequestData,
  FieldRow,
  ScenarioCoreDetails,
  TariffChanges,
  ClientDetails,
  ClientFamily,
  ClientFamilyItem,
  DisplayConfig,
  FieldConfigRow,
  PortfolioBreakdown,
} from './types';
import { FormFieldConfig, Validation } from '@/types/Client';
import { ChildLevel, RootLevel } from '@/types/Directory';
import { PortfolioStatus, PortfolioType, ApprovalStatus } from '@/enums/Global';

export type Getters = {
  getPortfolios(state: State): Array<Portfolio>;
  getPortfoliosIsLoading(state: State): boolean;
  getUnderperformingPortfoliosByClient(state: State): Array<GroupedPortfolio>;
  getUnderperformingPortfolioIds(state: State): Array<string>;
  getPortfolioByFieldMapping(
    state: State,
    fieldMappingPath: string
  ): Array<FormFieldConfig>;
  getPortfolio(state: State): Portfolio | null;
  getPortfolioIsLoading(state: State): boolean;
  getPortfolioColumnsByFieldMapping(
    state: State,
    fieldMappingPath: string
  ): PortfolioBreakdown;
  getPortfolioDirectory(state: State): Array<RootLevel>;
  getScenario(state: State): ScenarioCoreDetails | null;
  getScenarioIsLoading(state: State): boolean;
  getPricingColumnsByFieldMapping(
    state: State,
    fieldMappingPath: string
  ): Array<FieldRow>;
  getApprovalRequestData(state: State): ApprovalRequestData;
  getApprovalStatus(state: State): string;
  getTariffChanges(state: State): TariffChanges;
  getClientDetails(state: State): ClientDetails;
  getClientFamily(state: State): ClientFamily;
  getClientIsLoading(state: State): boolean;
  getClientError(state: State): boolean;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getPortfolios: state => {
    const portfoliosState = state.portfolios;
    return map(
      portfoliosState,
      item =>
        ({
          status: formatDataValue(item.status),
          portfolioId: formatDataValue(item.portfolioId),
          familyId: formatDataValue(item.familyId),
          assetsUnderManagement: formatDataValue(item.aum),
          clientId: formatDataValue(item.clientId),
        } as Portfolio)
    );
  },
  getPortfoliosIsLoading: state => state.portfoliosIsLoading,
  getUnderperformingPortfoliosByClient: state => {
    const portfoliosState = state.portfolios;
    const portfolios = map(
      portfoliosState,
      item =>
        ({
          status: formatDataValue(item.status),
          portfolioId: formatDataValue(item.portfolioId),
          familyId: formatDataValue(item.familyId),
          assetsUnderManagement: formatDataValue(item.aum),
          clientId: formatDataValue(item.clientId),
        } as Portfolio)
    );

    const groupedPortfolios = groupBy(portfolios, 'clientId');

    return reduce(
      Object.keys(groupedPortfolios),
      (result, clientId) => {
        result.push({ clientId, portfolios: groupedPortfolios[clientId] });
        return result;
      },
      [] as Array<GroupedPortfolio>
    );
  },
  getUnderperformingPortfolioIds: state => {
    const portfoliosState = state.portfolios;
    return map(
      portfoliosState,
      item => formatDataValue(item.portfolioId) as string
    );
  },
  getPortfolio: state => {
    const portfolioState = state.portfolio;
    if (isEmpty(portfolioState)) return null;
    const portfolio: Portfolio = {
      status: get(portfolioState, 'status.dataValue', null) as PortfolioStatus,
      portfolioId: get(portfolioState, 'portfolioId.dataValue', null) as string,
      familyId: get(portfolioState, 'familyId.dataValue', null) as string,
      clientId: get(portfolioState, 'clientId.dataValue', null) as string,
      bankerId: get(portfolioState, 'bankerId.dataValue', null) as string,
      canHaveScenarios: get(
        portfolioState,
        'canHaveScenarios.dataValue',
        null
      ) as boolean,
      canHaveOpportunities: get(
        portfolioState,
        'canHaveOpportunities.dataValue',
        null
      ) as boolean,
      canHaveProspects: get(
        portfolioState,
        'canHaveProspects.dataValue',
        null
      ) as boolean,
    };
    return portfolio;
  },
  getPortfolioIsLoading: state => state.portfolioIsLoading,
  getPortfolioByFieldMapping: (state, fieldMappingPath) => {
    const portfolioState = state.portfolio;
    const standardFeesState = state.standardFees;
    const scenarioState = state.scenario;
    const fieldMapping = get(config.layout, fieldMappingPath, []);
    if (isEmpty(fieldMapping)) {
      return [];
    }

    return map(fieldMapping.fields, field => {
      const displayConfig = fieldMapping.displayConfig[field.path];
      const fieldConfigPath = get(displayConfig, 'overridePath', field.path);
      const portfolioValue = get(portfolioState, fieldConfigPath, {});
      const standardFee = get(standardFeesState, fieldConfigPath, {});

      const fieldConfig: FormFieldConfig | null = !isEmpty(scenarioState)
        ? scenarioState.fieldConfig[field.path]
        : null;

      // if this field has a discount option, add discount config
      if (
        scenarioState.scenario &&
        displayConfig &&
        displayConfig.discountType
      ) {
        const fieldBasePath = field.path.substring(
          0,
          field.path.lastIndexOf('.')
        );

        const discountTypePath = fieldBasePath.concat('.', 'discountType');
        const discountType = scenarioState.fieldConfig[discountTypePath];
        const selectedDiscountType =
          discountType &&
          discountType.selectedValue &&
          discountType.selectedValue.value;

        const minimumFeePath = fieldBasePath.concat('.', 'minimumFee');
        const feeDiscountPath = fieldBasePath.concat('.', 'feeDiscount');
        const fixedRateDiscountPath = fieldBasePath.concat(
          '.',
          'fixedRateDiscount'
        );

        const minimumFee = scenarioState.fieldConfig[minimumFeePath];
        const feeDiscount = scenarioState.fieldConfig[feeDiscountPath];
        const fixedRateDiscount =
          scenarioState.fieldConfig[fixedRateDiscountPath];

        const discountTypeEnabled = !!(
          !minimumFee ||
          (discountType && discountType.selectedValue) ||
          minimumFee.selectedValue ||
          feeDiscount.selectedValue ||
          fixedRateDiscount.selectedValue
        );

        const discountConfig = {
          fieldKey: discountTypePath,
          selectedDiscountType,
          discountType: displayConfig.discountType,
          enabled: discountTypeEnabled,
        };

        displayConfig.discountConfig = discountConfig;
      }

      const fieldKeyPaths = field.path.split('.');
      fieldKeyPaths.pop();
      const parentPath = fieldKeyPaths.join('.');
      const parentPortfolio = get(portfolioState, parentPath, null);
      const parentStandardFee = get(standardFeesState, parentPath, null);

      const isLessThan = {
        portfolioValue: get(parentPortfolio, 'isLessThan', null),
        standardFee: get(parentStandardFee, 'isLessThan', null),
      };

      const feeScale = {
        portfolioValue: get(parentPortfolio, 'feeScale', null),
        standardFee: get(parentStandardFee, 'feeScale', null),
      };

      const imageForScale = get(parentStandardFee, 'imageForScale', null);

      let updateKeys: string[] = [];

      // add any dependencies to the update keys
      if (fieldConfig) {
        updateKeys = updateKeys.concat(fieldConfig.updateKey);
        if (displayConfig && displayConfig.dependencies) {
          forEach(displayConfig.dependencies, dependency => {
            updateKeys = updateKeys.concat(
              scenarioState.fieldConfig[dependency].updateKey
            );
          });
        }
      }

      return {
        fieldKey: field.path,
        ...displayConfig,
        ...fieldConfig,
        portfolioValue,
        standardFee,
        isLessThan,
        feeScale,
        updateKey: updateKeys,
        imageForScale,
      } as FormFieldConfig;
    });
  },
  getPortfolioColumnsByFieldMapping: (state, fieldMappingPath) => {
    const portfolioState = state.portfolio;
    const scenarioState = state.scenario;

    const fieldMapping = get(
      config.layout,
      fieldMappingPath,
      {}
    ) as FieldConfigRow;

    if (isEmpty(fieldMapping)) return {} as PortfolioBreakdown;

    const groupValidationConfig = get(
      fieldMapping,
      'groupValidationConfig',
      {}
    );
    const fieldsNotAffectingSimulationConfig = get(
      fieldMapping,
      'fieldsNotAffectingSimulation',
      []
    );
    let hasNotAffectingSimulationFields = false;

    const rows = fieldMapping.rows.map(row => {
      const warningsOnChange: string[] = [];

      let notAffectingSimulation = false;
      if (fieldsNotAffectingSimulationConfig.length > 0) {
        notAffectingSimulation = fieldsNotAffectingSimulationConfig.includes(
          row.heading
        );
        if (notAffectingSimulation) {
          hasNotAffectingSimulationFields = true;
        }
      }

      const columns = row.fields.map(fieldPath => {
        const displayConfig = get(
          fieldMapping.displayConfig,
          fieldPath,
          {}
        ) as DisplayConfig;
        const fieldConfigPath = get(displayConfig, 'overridePath', fieldPath);
        const portfolioValue = get(portfolioState, fieldConfigPath, {});

        const fieldConfig = !isEmpty(scenarioState)
          ? scenarioState.fieldConfig[fieldPath]
          : null;

        // add warning messages
        if (
          displayConfig.warningOnSelectionTKey &&
          fieldConfig &&
          fieldConfig.selectedValue
        ) {
          warningsOnChange.push(displayConfig.warningOnSelectionTKey);
        }

        // add validation rules
        let validationConfig =
          (fieldConfig && fieldConfig.validationConfig) || ({} as Validation);
        let groupValidations = {} as Validation;
        if (groupValidationConfig) {
          const fieldHeadingName = fieldPath.split('.');
          groupValidations = get(
            groupValidationConfig,
            fieldHeadingName[fieldHeadingName.length - 1],
            {}
          ) as Validation;
          validationConfig = merge(validationConfig, groupValidations);
        }

        // add any dependencies to the update keys, we want to update those too
        let updateKeys: string[] = [];
        if (fieldConfig) {
          updateKeys = updateKeys.concat(fieldConfig.updateKey);
          if (row.dependencies && row.dependencies.length > 0) {
            updateKeys = updateKeys.concat(row.dependencies);
          }
        }

        return {
          fieldKey: fieldPath,
          portfolioValue,
          displayType: displayConfig.displayType,
          fieldType: displayConfig.fieldType,
          selectedValue: fieldConfig && fieldConfig.selectedValue,
          updateKey: updateKeys,
          validationConfig,
        };
      });

      return {
        heading: row.heading,
        displayType: row.displayType,
        columns,
        messages: {
          warningsOnChange: warningsOnChange,
        },
        notAffectingSimulation,
        showBorder: row.showBorder,
        visibility: row.visibility,
        moneySegments: {
          id: row.moneySegmentsPath,
          segments: get(portfolioState, row.moneySegmentsPath, []),
        },
      };
    });

    return {
      headings: get(fieldMapping, 'headings', []),
      rows,
      hasNotAffectingSimulationFields,
    } as PortfolioBreakdown;
  },
  getPortfolioDirectory: state => {
    const portfoliosState = state.portfolios;
    const scenariosState = state.scenarios;
    const portfoliosAndScenarios = map(portfoliosState, portfolio => {
      const scenarios = scenariosState
        .filter(
          scenario =>
            scenario.portfolioId.dataValue === portfolio.portfolioId.dataValue
        )
        .map(scenario => {
          return {
            id: formatDataValue(scenario.scenarioId) as string,
            title: formatDataValue(scenario.name) as string,
            createdBy: formatDataValue(scenario.createdBy) as string,
            createdAt: formatDataValue(scenario.createdAt) as string,
            status: formatDataValue(
              scenario.simulatedHeatmapStatus
            ) as PortfolioStatus,
            familyStatus: formatDataValue(
              scenario.familySimulatedHeatmapStatus
            ) as PortfolioStatus,
            isDeletable: scenario.isDeletable.dataValue as boolean,
            isEditable: scenario.isEditable.dataValue as boolean,
            approvalStatus: formatDataValue(
              scenario.approvalStatus
            ) as ApprovalStatus,
          } as ChildLevel;
        });
      return {
        id: formatDataValue(portfolio.portfolioId) as string,
        title: formatDataValue(portfolio.portfolioId) as string,
        isBold: portfolio.isMainPortfolio,
        subline: formatDataValue(portfolio.aum) as string,
        status: formatDataValue(portfolio.status) as string,
        children: scenarios,
        type: PortfolioType.PORTFOLIO,
      } as RootLevel;
    });
    return portfoliosAndScenarios;
  },
  getScenario: state => {
    const scenarioState = state.scenario;
    if (isEmpty(scenarioState) || isEmpty(scenarioState.scenario)) return null;
    const scenario = state.scenario.scenario;
    const scenarioCoreDetails: ScenarioCoreDetails = {
      approvalStatus: formatDataValue(
        scenario.approvalStatus
      ) as ApprovalStatus,
      simulatedHeatmapStatus: formatDataValue(
        scenario.simulatedHeatmapStatus
      ) as PortfolioStatus,
      portfolioId: formatDataValue(
        scenarioState.scenario.portfolioId
      ) as string,
      clientId: formatDataValue(scenarioState.scenario.clientId) as string,
      scenarioId: formatDataValue(scenario.scenarioId) as string,
      name: formatDataValue(scenario.name) as string,
      createdBy: formatDataValue(scenario.createdBy) as string,
      createdAt: formatDataValue(scenario.createdAt) as string,
      isDeletable: get(scenario, 'isDeletable.dataValue', false) as boolean,
      isEditable: get(scenario, 'isEditable.dataValue', false) as boolean,
    };
    return scenarioCoreDetails;
  },
  getScenarioIsLoading: state => state.scenarioIsLoading,
  getPricingColumnsByFieldMapping: (state, fieldMappingPath) => {
    const pricingState = state.scenario;
    const scenarioState = state.scenario;

    const fieldMapping = get(
      config.layout,
      fieldMappingPath,
      {}
    ) as FieldConfigRow;

    if (isEmpty(pricingState) || isEmpty(fieldMapping)) return [];

    return fieldMapping.rows.map(row => {
      const columns = row.fields.map(fieldPath => {
        const displayConfig = fieldMapping.displayConfig[fieldPath];
        const fieldConfigPath = get(displayConfig, 'overridePath', fieldPath);
        const portfolioValue = get(pricingState, fieldConfigPath, {
          dataValue: null,
          dataType: null,
        });
        const fieldConfig = !isEmpty(scenarioState)
          ? scenarioState.fieldConfig[fieldPath]
          : null;

        return {
          fieldKey: fieldPath,
          ...fieldConfig,
          ...displayConfig,
          portfolioValue,
          updateKey: (fieldConfig && fieldConfig.updateKey) || [fieldPath],
        } as FormFieldConfig;
      });

      return {
        heading: row.heading,
        displayType: row.displayType,
        headerComponentType: row.headerComponentType,
        columns,
        visibility: row.visibility,
        messages: {
          warningsOnChange: [],
        },
      };
    });
  },
  getApprovalRequestData: state => {
    const pricingState = state.scenario;

    return {
      new: {
        status: formatDataValue(get(pricingState, 'pricing.new.status', undefined)),
        total: formatDataValue(
          get(pricingState, 'pricing.new.grandTotalFees', undefined)
        ),
      },
      forecast: {
        status: formatDataValue(
          get(pricingState, 'pricing.forecast.status', undefined)
        ),
        total: formatDataValue(
          get(pricingState, 'pricing.forecast.grandTotalFees', undefined)
        ),
      },
    } as ApprovalRequestData;
  },
  getApprovalStatus: state => {
    const pricingState = state.scenario;
    return formatDataValue(
      get(pricingState, 'scenario.approvalStatus', undefined)
    ) as string;
  },
  getTariffChanges: state => {
    const settings = Object.fromEntries(
      Object.entries(state.scenario.fieldConfig).filter(([key]) =>
        key.startsWith('settings.')
      )
    );

    const managementType =
      settings['settings.membershipPackage'] &&
      !!settings['settings.membershipPackage'].selectedValue;

    const serviceLevel =
      settings['settings.serviceLevel'] &&
      !!settings['settings.serviceLevel'].selectedValue;

    const portfolioSettings = Object.values(settings).some(
      item => item.selectedValue != null
    );

    return {
      managementType,
      serviceLevel,
      portfolioSettings,
    };
  },
  getClientDetails: state => {
    const clientDetails = state.clientDetails;
    return {
      familyId: formatDataValue(clientDetails.familyId),
      openingDate: formatDataValue(clientDetails.openingDate),
      country: formatDataValue(clientDetails.country),
      teamId: formatDataValue(clientDetails.teamId),
    } as ClientDetails;
  },
  getClientFamily: state => {
    const clientFamily = state.clientFamily.familyList;
    return {
      familyList: map(
        clientFamily,
        item =>
          ({
            roa: formatDataValue(item.roa),
            aum: formatDataValue(item.aum),
            status: formatDataValue(item.status),
            clientId: formatDataValue(item.clientId),
          } as ClientFamilyItem)
      ),
    } as ClientFamily;
  },
  getClientIsLoading: state => state.clientIsLoading,
  getClientError: state => state.clientError,
};
